import { button, element } from "./dom";

export function dashboardHeader(
  brand: Node,
  displayName: string,
  accountDescription: string,
  ...actions: readonly Node[]
): HTMLElement {
  const logo = element("a", "app-header__logo", brand);
  logo.href = "https://groundspark.app/";
  logo.setAttribute("aria-label", "Groundspark home");
  return element(
    "header",
    "app-header",
    logo,
    element(
      "div",
      "account-bar",
      element(
        "div",
        "account-copy",
        element("strong", null, displayName),
        element("span", null, accountDescription),
      ),
      ...actions,
    ),
  );
}

export function dashboardSelector(
  label: string,
  options: readonly { readonly id: string; readonly name: string }[],
  selected: string,
  disabled: boolean,
  onChange: (select: HTMLSelectElement) => void | Promise<void>,
): HTMLLabelElement {
  const select = element("select", "select");
  select.setAttribute("aria-label", label);
  for (const option of options) {
    const item = element("option", null, option.name);
    item.value = option.id;
    item.selected = option.id === selected;
    select.append(item);
  }
  select.disabled = disabled;
  select.addEventListener("change", () => void onChange(select));
  return element("label", "dashboard-selector", element("span", null, `${label}:`), select);
}

export interface DashboardTab<T extends string> {
  readonly id: T;
  readonly label: string;
  readonly count?: string;
}

export function dashboardTabs<T extends string>(
  label: string,
  tabs: readonly DashboardTab<T>[],
  activeTab: T,
  onSelect: (tab: T) => void | Promise<void>,
): HTMLElement {
  const tabList = element("nav", "tabs");
  tabList.setAttribute("role", "tablist");
  tabList.setAttribute("aria-label", label);
  for (const { id, label, count } of tabs) {
    const control = button(label, "button button--white", () => onSelect(id));
    control.id = `tab-${id}`;
    control.setAttribute("role", "tab");
    control.setAttribute("aria-selected", String(activeTab === id));
    control.setAttribute("aria-controls", `panel-${id}`);
    control.tabIndex = activeTab === id ? 0 : -1;
    if (count !== undefined) control.append(element("span", "tab__count", count));
    tabList.append(control);
  }
  tabList.addEventListener("keydown", (event) => {
    const index = tabs.findIndex(({ id }) => id === activeTab);
    let nextIndex: number;
    switch (event.key) {
      case "ArrowLeft":
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case "ArrowRight":
        nextIndex = (index + 1) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }
    const next = tabs[nextIndex];
    if (next === undefined) return;
    event.preventDefault();
    void onSelect(next.id);
  });
  return tabList;
}
